import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Banknote,
  Bell,
  Calendar,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  CreditCard,
  FileText,
  Package,
  Receipt,
  Timer,
} from "lucide-react";
import { Pill } from "../ui/componentes";
import { atrasado, brl, dias, fdata } from "../ui/formato";

/* ══════════════════════════════════════════════════════════════════
   APLICAÇÃO
   ══════════════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════════
   NOTIFICAÇÕES
   Lidas do estado, não de uma caixa de mensagens. Cada aviso é um
   problema aberto: ele some sozinho quando o problema é resolvido.
   ══════════════════════════════════════════════════════════════════ */
function Notificacoes({ db, usuario, ir, abrirOS }) {
  const [aberto, setAberto] = useState(false);
  const caixa = useRef(null);

  useEffect(() => {
    if (!aberto) return;
    const fora = (e) => { if (caixa.current && !caixa.current.contains(e.target)) setAberto(false); };
    const esc = (e) => e.key === "Escape" && setAberto(false);
    document.addEventListener("mousedown", fora);
    window.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", fora); window.removeEventListener("keydown", esc); };
  }, [aberto]);

  const avisos = useMemo(() => {
    const v = [];
    const fin = usuario.papel !== "mecanico";
    const cli = (id) => db.clientes.find((c) => c.id === id);
    const vei = (id) => db.veiculos.find((x) => x.id === id);
    const total = (o) => o.itens.reduce((s, i) => s + i.qtd * i.valor, 0) - (o.desconto || 0);

    // ── crítico
    const zeradas = db.pecas.filter((p) => p.qtd === 0);
    if (zeradas.length) v.push({
      id: "pz", nivel: 2, icone: Package,
      titulo: `${zeradas.length} peça(s) zerada(s)`,
      texto: zeradas.slice(0, 3).map((p) => p.nome).join(", ") + (zeradas.length > 3 ? "…" : ""),
      acao: "Ver estoque", ir: () => ir("estoque"),
    });

    const atrasadas = db.os.filter((o) => o.previsao < dias(0) && !["entregue", "recusada"].includes(o.status));
    for (const o of atrasadas.slice(0, 3)) {
      const dd = Math.round((new Date(dias(0)) - new Date(o.previsao)) / 86400000);
      v.push({
        id: "os" + o.id, nivel: 2, icone: Timer,
        titulo: `${o.id} passou da previsão`,
        texto: `${vei(o.vei)?.marca} ${vei(o.vei)?.modelo} · ${cli(o.cli)?.nome.split(" ")[0]} · ${dd} dia(s) de atraso`,
        acao: "Abrir OS", ir: () => abrirOS(o.id),
      });
    }

    if (fin) {
      const vencidos = db.fin.filter((f) => !f.pago && atrasado(f.venc));
      const receber = vencidos.filter((f) => f.tipo === "receber");
      const pagar = vencidos.filter((f) => f.tipo === "pagar");
      if (receber.length) v.push({
        id: "fr", nivel: 2, icone: Banknote,
        titulo: `${brl(receber.reduce((s, f) => s + f.valor, 0))} vencidos a receber`,
        texto: `${receber.length} título(s) em atraso. O mais antigo venceu em ${fdata(receber.map((f) => f.venc).sort()[0])}.`,
        acao: "Cobrar", ir: () => ir("financeiro"),
      });
      if (pagar.length) v.push({
        id: "fp", nivel: 2, icone: CreditCard,
        titulo: `${brl(pagar.reduce((s, f) => s + f.valor, 0))} vencidos a pagar`,
        texto: `${pagar.length} conta(s) em atraso.`,
        acao: "Ver contas", ir: () => ir("financeiro"),
      });
    }

    // ── atenção
    const baixas = db.pecas.filter((p) => p.qtd > 0 && p.qtd <= p.min);
    if (baixas.length) v.push({
      id: "pb", nivel: 1, icone: Package,
      titulo: `${baixas.length} peça(s) abaixo do mínimo`,
      texto: baixas.slice(0, 3).map((p) => `${p.nome} (${p.qtd} ${p.un})`).join(", ") + (baixas.length > 3 ? "…" : ""),
      acao: "Gerar pedido", ir: () => ir("estoque"),
    });

    const orcAbertos = (db.orcamentos || []).filter((o) => {
      if (o.status !== "enviado") return false;
      const d = new Date(o.criado); d.setDate(d.getDate() + o.validade);
      return d.toISOString().slice(0, 10) >= dias(0);
    });
    if (fin && orcAbertos.length) v.push({
      id: "orc", nivel: 1, icone: Receipt,
      titulo: `${orcAbertos.length} orçamento(s) sem resposta`,
      texto: `${brl(orcAbertos.reduce((s, o) => s + o.itens.reduce((t, i) => t + i.qtd * i.valor, 0) - (o.desconto || 0), 0))} esperando o cliente decidir.`,
      acao: "Cobrar retorno", ir: () => ir("orcamentos"),
    });

    const orcVencendo = (db.orcamentos || []).filter((o) => {
      if (o.status !== "enviado") return false;
      const d = new Date(o.criado); d.setDate(d.getDate() + o.validade);
      const iso = d.toISOString().slice(0, 10);
      return iso >= dias(0) && iso <= dias(3);
    });
    if (fin && orcVencendo.length) v.push({
      id: "orcv", nivel: 2, icone: Timer,
      titulo: `${orcVencendo.length} orçamento(s) vencendo`,
      texto: orcVencendo.map((o) => o.nome.split(" ")[0]).join(", ") + " — depois do prazo o preço da peça já mudou.",
      acao: "Ver orçamentos", ir: () => ir("orcamentos"),
    });

    const esperando = db.os.filter((o) => o.status === "aguardando");
    for (const o of esperando.slice(0, 3)) v.push({
      id: "ag" + o.id, nivel: 1, icone: Clock,
      titulo: `${o.id} esperando o cliente`,
      texto: `${cli(o.cli)?.nome} · ${brl(total(o))} · enviado em ${fdata(o.abertura)}`,
      acao: "Cobrar retorno", ir: () => abrirOS(o.id),
    });

    if (fin) {
      const semNota = db.os.filter((o) => {
        if (!["finalizada", "entregue"].includes(o.status)) return false;
        const s = o.itens.some((i) => i.tipo === "servico") && !o.nfServico;
        const p = o.itens.some((i) => i.tipo === "peca") && !o.nfPeca;
        return s || p;
      });
      if (semNota.length) v.push({
        id: "nf", nivel: 1, icone: FileText,
        titulo: `${semNota.length} OS concluída(s) sem nota`,
        texto: "Serviço entregue e não faturado é dinheiro parado.",
        acao: "Emitir", ir: () => ir("notas"),
      });

      const travadas = db.notas.filter((n) => n.status === "processando");
      if (travadas.length) v.push({
        id: "np", nivel: 1, icone: Receipt,
        titulo: `${travadas.length} nota(s) aguardando a SEFAZ`,
        texto: travadas.map((n) => n.id).join(", "),
        acao: "Acompanhar", ir: () => ir("notas"),
      });

      const vencendo = db.fin.filter((f) => !f.pago && !atrasado(f.venc) && f.venc <= dias(3));
      if (vencendo.length) v.push({
        id: "fv", nivel: 0, icone: Calendar,
        titulo: `${vencendo.length} vencimento(s) nos próximos 3 dias`,
        texto: `${brl(vencendo.reduce((s, f) => s + f.valor, 0))} entre entradas e saídas.`,
        acao: "Ver agenda", ir: () => ir("financeiro"),
      });
    }

    // ── informativo
    const criticos = db.checklists.filter((k) =>
      Object.values(k.itens).includes("crit") && !db.os.some((o) => o.checklist === k.id));
    for (const k of criticos.slice(0, 2)) v.push({
      id: "ck" + k.id, nivel: 0, icone: ClipboardCheck,
      titulo: `Checklist ${k.id} com item crítico e sem OS`,
      texto: `${vei(k.vei)?.marca} ${vei(k.vei)?.modelo} — vale abrir o orçamento.`,
      acao: "Ver recepção", ir: () => ir("recepcao"),
    });

    return v.sort((a, b) => b.nivel - a.nivel);
  }, [db, usuario, ir, abrirOS]);

  const urgentes = avisos.filter((a) => a.nivel === 2).length;
  const cor = (n) => (n === 2 ? "crit" : n === 1 ? "signal" : "gulf-d");
  const fundo = (n) => (n === 2 ? "var(--crit-l)" : n === 1 ? "var(--signal-l)" : "var(--gulf-l)");

  return (
    <div className="relative" ref={caixa}>
      <button
        onClick={() => setAberto(!aberto)}
        aria-label={avisos.length ? `${avisos.length} aviso(s) em aberto` : "Nenhum aviso"}
        aria-expanded={aberto}
        className="flex items-center gap-1.5 px-2.5 py-1.5"
        style={{
          background: aberto ? "var(--ink)" : "var(--el)",
          color: aberto ? "#fff" : "var(--txt)",
          borderRadius: "var(--r)",
          border: `1px solid ${aberto ? "var(--ink)" : "var(--line)"}`,
          cursor: "pointer", transition: "background .13s,border-color .13s",
        }}>
        <Bell size={14} strokeWidth={2.2} style={{ color: aberto ? "#fff" : "var(--mut)" }} />
        {avisos.length > 0 && (
          <span className="mono text-xs font-semibold" style={{
            background: urgentes ? "var(--crit)" : "var(--signal)",
            color: "#fff", borderRadius: 3, padding: "1px 5px", lineHeight: 1.4,
          }}>{avisos.length}</span>
        )}
      </button>

      {aberto && (
        <div className="card rise" style={{
          position: "absolute", right: 0, top: "calc(100% + 8px)", width: 356, zIndex: 55,
          boxShadow: "0 16px 44px rgba(0,0,0,.19)", overflow: "hidden",
        }}>
          <div className="stripe" />
          <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid var(--line)" }}>
            <span className="dsp" style={{ fontSize: 15 }}>Avisos</span>
            {urgentes > 0 && <Pill t="crit">{urgentes} urgente{urgentes > 1 ? "s" : ""}</Pill>}
          </div>

          {avisos.length === 0 ? (
            <div className="flex flex-col items-center text-center px-6 py-9">
              <CheckCircle2 size={26} className="t-ok mb-2.5" />
              <div className="dsp" style={{ fontSize: 15 }}>Nada pendente</div>
              <div className="text-sm t-mut mt-1">Estoque abastecido, contas em dia e nenhuma OS atrasada.</div>
            </div>
          ) : (
            <div className="scroll" style={{ maxHeight: 396, overflowY: "auto" }}>
              {avisos.map((a) => (
                <button key={a.id}
                  onClick={() => { a.ir(); setAberto(false); }}
                  className="flex items-start gap-3 px-4 py-3 text-left w-full"
                  style={{ borderBottom: "1px solid var(--line)", background: "var(--card)", cursor: "pointer", border: "none", borderBottomWidth: 1, borderBottomStyle: "solid", borderBottomColor: "var(--line)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--el)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "var(--card)")}>
                  <span className="flex items-center justify-center flex-none" style={{
                    width: 28, height: 28, borderRadius: 5,
                    background: fundo(a.nivel), color: `var(--${cor(a.nivel)})`,
                  }}>
                    <a.icone size={15} strokeWidth={2.2} />
                  </span>
                  <span className="flex-1" style={{ minWidth: 0 }}>
                    <span className="block text-sm font-semibold leading-snug">{a.titulo}</span>
                    <span className="block text-xs t-mut mt-0.5 leading-snug">{a.texto}</span>
                    <span className="block text-xs font-semibold mt-1.5" style={{ color: `var(--${cor(a.nivel)})` }}>{a.acao} →</span>
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="px-4 py-2.5 text-xs t-mut2" style={{ background: "var(--el)", borderTop: "1px solid var(--line)" }}>
            Os avisos somem sozinhos quando o problema é resolvido.
          </div>
        </div>
      )}
    </div>
  );
}

export { Notificacoes };
